/*
  This file is part of the project: "Digiziege"
  An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
  GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

  Copyright (c) 2026 Digiziege Team TH-Bingen

  Description:
  Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
  master data management and health tracking.
  Built as a PWA with a local-first approach to ensure data privacy.

  LICENSE INFORMATION:
  This program is free software: you can redistribute it and/or modify it under the terms of the
  GNU Affero General Public License as published by the Free Software Foundation,
  either version 3 of the License, or (at your option) any later version.

  This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
  without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
  See the GNU Affero General Public License for more details.

  You should have received a copy of the GNU Affero General Public License along with this program.
  If not, see <https://www.gnu.org/licenses/>.
*/

/**
 * useDeviceOrientation.js
 *
 * Track device tilt angle using device orientation sensors
 * Based on master thesis AngleProviderSensor implementation
 *
 * Extended to handle portrait mode: thesis used square camera (640x640)
 * Switching sensor axis based on orientation, to support every format
 */

import { ref } from 'vue'

/**
 * Composable for tracking device orientation angle
 *
 * Thesis used gamma only with square camera format.
 *
 * This implementation support both orientations:
 * - Landscape: 90 + gamma (left/right tilt) matches thesis
 * - Portrait: 90 - beta (forward/backward tilt) required for rotated device
 *
 * @param {number} defaultAngle - Fallback if sensor unavailable (default: 20)
 * @returns {Object} { angle, hasSupport, isTracking, isInitializing, isPortrait, startTracking, stopTracking }
 */
export function useDeviceOrientation(defaultAngle = 20) {
  const angle = ref(defaultAngle)
  const hasSupport = ref(false)
  const isTracking = ref(false)
  const isInitializing = ref(false)
  const isPortrait = ref(window.innerHeight > window.innerWidth)

  let handler = null
  let orientationChangeHandler = null

  /**
   * Start tracking device orientation
   * Requests permission on iOS if needed
   */
  async function startTracking() {
    if (isTracking.value) {
      return true
    }

    // Check if DeviceOrientation API is supported
    if (!window.DeviceOrientationEvent) {
      console.warn(
        '[DeviceOrientation] API not supported, using default angle:',
        defaultAngle
      )
      hasSupport.value = false
      return false
    }

    // Request permission on iOS 13+
    if (typeof DeviceOrientationEvent.requestPermission === 'function') {
      isInitializing.value = true
      try {
        const permissionState = await DeviceOrientationEvent.requestPermission()
        isInitializing.value = false

        if (permissionState === 'granted') {
          startListening()
          return true
        } else {
          console.warn('[DeviceOrientation] Permission denied')
          hasSupport.value = false
          return false
        }
      } catch (err) {
        isInitializing.value = false
        console.warn('[DeviceOrientation] Permission request failed:', err)
        hasSupport.value = false
        return false
      }
    } else {
      // Non-iOS or older iOS, start directly
      startListening()
      return true
    }
  }

  /**
   * Stop tracking device orientation
   */
  function stopTracking() {
    stopListening()
  }

  function startListening() {
    handler = (event) => {
      const currentIsPortrait = window.innerHeight > window.innerWidth
      isPortrait.value = currentIsPortrait

      // Thesis: angle = 90 + gamma (landscape only with square camera)
      // Handle both:
      // - gamma for landscape (left/right tilt)
      // - beta for portrait (forward/backward tilt, since device is rotated)

      if (currentIsPortrait) {
        if (event.beta !== null) {
          // Portrait: beta > 0 = tilted down, beta < 0 = tilted up
          angle.value = 90 - event.beta
          hasSupport.value = true
        }
      } else {
        if (event.gamma !== null) {
          // Landscape: thesis formula
          angle.value = 90 + event.gamma
          hasSupport.value = true
        }
      }
    }

    // Track orientation changes
    orientationChangeHandler = () => {
      isPortrait.value = window.innerHeight > window.innerWidth
    }

    window.addEventListener('deviceorientation', handler)
    window.addEventListener('resize', orientationChangeHandler)
    isTracking.value = true
  }

  function stopListening() {
    if (handler) {
      window.removeEventListener('deviceorientation', handler)
      handler = null
      isTracking.value = false
    }
    if (orientationChangeHandler) {
      window.removeEventListener('resize', orientationChangeHandler)
      orientationChangeHandler = null
    }
  }

  return {
    angle,
    hasSupport,
    isTracking,
    isInitializing,
    isPortrait,
    startTracking,
    stopTracking,
  }
}
